import { Button, styled } from "@mui/material";
import { useEffect, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormTrigger,
  UseFormWatch,
  RegisterOptions,
  FieldPath
} from "react-hook-form";
import { useKeyPress } from "../../../../../hooks/useKeyPress";
import colors from "../../../../../theme/themeConstants";
import MarkOkIcon from "../../../../icons/Components/MarkOkIcon";
import CustomInput from "../../../CustomInput";
import { ISignUpFieldValues } from "../AuthSignUp";

interface IProps {
  next: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  watch: UseFormWatch<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

const MIN_PASS_LENGTH = 8;
const MAX_PASS_LENGTH = 128;
// https://owasp.org/www-community/OWASP_Validation_Regex_Repository
const passwordRegexp = new RegExp(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,128}$/);

const SignUpPasswordStep = (props: IProps) => {
  const { next, control, trigger, watch, getValues } = props;

  const [passLengthIsOk, setPassLengthIsOk] = useState(false);
  const [passSymbolsIsOk, setPassSymbolsIsOk] = useState(false);

  const enterPressed = useKeyPress("Enter");

  const handleNext = async () => {
    if (await trigger("password") && await trigger("password_confirm")) {
      next();
    }
  };

  const validate = (val: string) => {
    if (val.length < MIN_PASS_LENGTH || val.length > MAX_PASS_LENGTH) {
      setPassLengthIsOk(false);
    } else {
      setPassLengthIsOk(true);
    }

    if (passwordRegexp.test(val)) {
      setPassSymbolsIsOk(true);
    } else {
      setPassSymbolsIsOk(false);
    }
  };

  useEffect(() => {
    validate(getValues().password);
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
    // eslint-disable-next-line
  }, [enterPressed])

  return (
    <Container>
      <Controller
        name="password"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
          },
          minLength: {
            value: MIN_PASS_LENGTH,
          },
          maxLength: {
            value: MAX_PASS_LENGTH,
          },
          pattern: {
            value: passwordRegexp,
          },
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Password</label>
            <CustomInput
              value={field.value}
              type="password"
              onChange={async (e) => {
                field.onChange(e);
                validate(e.target.value);
                await trigger("password");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
            />
          </Block>
        )}
      />
      <FlexBlock>
        <MarkOkIcon color={passLengthIsOk ? "green" : "#FF536D"}/>
        <span>Must have {MIN_PASS_LENGTH} characters in length</span>
      </FlexBlock>
      <FlexBlock>
        <MarkOkIcon color={passSymbolsIsOk ? "green" : "#FF536D"}/>
        <span>Lower case (a-z), upper case (A-Z) and numbers (0-9)</span>
      </FlexBlock>
      <Controller
        name="password_confirm"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Required Field!"
          },
          validate: {
            value: (value) => {
              return value === watch<FieldPath<ISignUpFieldValues>>("password") || "Passwords do not match!";
            }
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Confirm Password</label>
            <CustomInput
              value={field.value}
              type="password"
              onChange={async (e) => {
                field.onChange(e);
                await trigger("password_confirm");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
            />
          </Block>
        )}
      />
      <Button variant="contained" fullWidth sx={{
        background: colors.purpleGradient,
        margin: "1rem 0 0rem 0",
        minHeight: "3rem",
        borderRadius: "38px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none"
        }
      }} onClick={handleNext}>
        <span>Next</span>
      </Button>
    </Container>
  );
};

export default SignUpPasswordStep;

const Container = styled("div")({
  marginTop: "2rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem"
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const FlexBlock = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "& span": {
    fontSize: "10px",
    color: colors.textDark,
    opacity: 0.5
  }
});
